import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ObservationAgreementSignatureSummary } from '../../api/types';
import { Pagination } from '../../components/Pagination';
import { clampPage, pageSlice } from '../../components/paginate';
import { useHubContext } from '../../shell/HubContext';
import { SignatureTable, type SortKey } from './SignatureTable';

/**
 * Whether a signature matches what was typed.
 *
 * Every whitespace-separated word has to appear somewhere, rather than the whole string having to
 * appear in one field — so "oak jamie" and "jamie oak" both find the same person. Same rule as the
 * other two dashboards, for the same reason.
 */
function matchesSearch(signature: ObservationAgreementSignatureSummary, terms: string[]): boolean {
  if (terms.length === 0) {
    return true;
  }

  // `?? ''` rather than letting a null stringify: "null" in the haystack would make a search for
  // that word match every signature that left the field blank.
  const haystack = `${signature.signerName} ${signature.email ?? ''} ${signature.location ?? ''}`
    .toLowerCase();

  return terms.every((term) => haystack.includes(term));
}

/**
 * Recruiting's view of the observation agreement — every signed copy, most recent first, with a row
 * opening the agreement as that person signed it.
 *
 * There are no actions on this page, and here that is more than a convention borrowed from the CT
 * assessment dashboard: a signature is a record that somebody agreed to a text at a moment, so
 * editing one would make it a record of nothing. The page is a reading surface and says so by having
 * no buttons that change anything.
 */
export function ObservationAgreementReviewSection() {
  const { api } = useHubContext();
  const navigate = useNavigate();

  const [signatures, setSignatures] = useState<ObservationAgreementSignatureSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('signedAtUtc');
  const [sortDescending, setSortDescending] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setSignatures(await api.listObservationAgreementSignatures(controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load signed agreements.');
        }
      }
    })();

    return () => controller.abort();
  }, [api]);

  const visible = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);

    return [...(signatures ?? [])]
      .filter((s) => matchesSearch(s, terms))
      .sort((a, b) => {
        // Same `?? ''`: a missing email sorted as the word "null" would land in the middle of the
        // names beginning with n. Empty sorts to one end, which is where "not given" belongs.
        const result = (a[sortKey] ?? '').localeCompare(b[sortKey] ?? '');
        return sortDescending ? -result : result;
      });
  }, [signatures, search, sortKey, sortDescending]);

  // Clamped on render rather than corrected in an effect: the list it indexes into shrinks as the
  // user types, and an effect would paint the empty page once before fixing it.
  const currentPage = clampPage(page, visible.length, pageSize);
  const rows = pageSlice(visible, currentPage, pageSize);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDescending((d) => !d);
    } else {
      setSortKey(key);
      setSortDescending(false);
    }

    // Sorting reorders the whole list, so page 3 of the old order means nothing in the new one.
    setPage(1);
  }

  function changeSearch(value: string) {
    setSearch(value);
    setPage(1);
  }

  function changePageSize(size: number) {
    // Keep the first row currently on screen visible, rather than jumping back to the top.
    const firstVisibleIndex = (currentPage - 1) * pageSize;
    setPageSize(size);
    setPage(Math.floor(firstVisibleIndex / size) + 1);
  }

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load signed agreements</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (signatures === null) {
    return <div className="page-message">Loading…</div>;
  }

  return (
    <div className="care-page care-page--wide">
      <header className="care-page__header">
        <h1 className="care-page__title">Completed Office Observation Agreements</h1>
        <p className="care-page__subtitle">
          Every Chiropractic Office Observation Agreement signed from the public page, most recent
          first. Open one to read it exactly as that person signed it.
        </p>
      </header>

      <div className="care-review__toolbar">
        <input
          className="control care-review__search"
          type="search"
          placeholder="Search name, email or clinic…"
          value={search}
          onChange={(e) => changeSearch(e.target.value)}
        />
        {search.trim().length > 0 && (
          <span className="care-review__count">
            {visible.length} match{visible.length === 1 ? '' : 'es'}
            <button type="button" className="care-document-link" onClick={() => changeSearch('')}>
              clear
            </button>
          </span>
        )}
      </div>

      <section className="care-review__group">
        <div className="care-review__group-header">
          <h2 className="care-review__group-title">
            Signed agreements
            {signatures.length > 0 && <span className="care-count">{signatures.length}</span>}
          </h2>
        </div>

        <SignatureTable
          signatures={rows}
          emptyMessage={
            signatures.length === 0
              ? 'Nobody has signed the agreement yet.'
              : 'No signed agreements match this search.'
          }
          sortKey={sortKey}
          sortDescending={sortDescending}
          onSort={toggleSort}
          onOpen={(signature) => navigate(`/observation-agreement-review/${signature.id}`)}
        />

        <Pagination
          totalItems={visible.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={changePageSize}
          itemNoun="agreements"
        />
      </section>
    </div>
  );
}
